import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithEmail, signInWithGoogle } from '@/lib/supabase';
import { formatAuthError, getFriendlyAuthError } from '@/lib/authErrors';
import { Button, Input, Card, CardBody } from '@/components/ui';
import { useResendConfirmation } from '@/hooks/useResendConfirmation';

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const { resend: handleResendEmail, cooldown: resendCooldown } = useResendConfirmation(email);

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const { error } = await signInWithEmail(email, password);

    if (error) {
      const friendly = getFriendlyAuthError(error);
      setShowResend(friendly.message === 'Email not verified');
      setError(friendly.suggestion ? `${friendly.message}. ${friendly.suggestion}` : friendly.message);
      setIsLoading(false);
    } else {
      navigate('/dashboard');
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    const { error } = await signInWithGoogle();
    if (error) {
      setError(formatAuthError(error));
    }
  };

  return (
    <div className="min-h-screen bg-background-primary flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-serif text-4xl mb-2">
            <span className="text-accent-primary">Koda</span>
          </h1>
          <p className="text-foreground-secondary">Sign in to continue learning</p>
        </div>

        <Card>
          <CardBody className="p-6">
            <form onSubmit={handleEmailSignIn} className="space-y-4">
              <Input
                type="email"
                label="Email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setShowResend(false); }}
                required
              />
              <div>
                <Input
                  type="password"
                  label="Password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <div className="mt-1 text-right">
                  <Link to="/forgot-password" className="text-sm text-accent-primary hover:underline">
                    Forgot password?
                  </Link>
                </div>
              </div>

              {error && (
                <div className="space-y-2">
                  <p className="text-sm text-red-500">{error}</p>
                  {showResend && (
                    <>
                      <p className="text-xs text-foreground-muted">Don't see it? Check your spam or junk folder.</p>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="w-full"
                        onClick={handleResendEmail}
                        disabled={resendCooldown > 0}
                      >
                        {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend confirmation email'}
                      </Button>
                    </>
                  )}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border-subtle" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-background-card text-foreground-muted">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="secondary"
              className="w-full"
              onClick={handleGoogleSignIn}
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </Button>

            <p className="text-center text-xs text-foreground-muted mt-3">
              By continuing with Google, you agree to our{' '}
              <Link to="/terms" className="text-accent-primary hover:underline" target="_blank">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-accent-primary hover:underline" target="_blank">
                Privacy Policy
              </Link>
            </p>

            <p className="text-center text-sm text-foreground-muted mt-6">
              Don't have an account?{' '}
              <Link to="/signup" className="text-accent-primary hover:underline">
                Sign up
              </Link>
            </p>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
